import clsx from 'clsx';
import { forwardRef, memo, type MouseEvent, type Ref } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { SharedIconProps } from '../../types/SharedIconProps';

import { CountBadge } from '../CountBadge/CountBadge';
import { Icon } from '../Icon/Icon';
import { Text } from '../Text/Text';
import './Chip.css';

const messages = defineMessages({
  dismiss: {
    id: 'Chip.dismiss',
    defaultMessage: 'Remove {label}',
    description: 'Accessible name for the chip dismiss control',
  },
});

export type ChipColor = 'azure' | 'coral' | 'indigo' | 'teal';

type ChipBaseProps = {
  /** Visible chip label. Also used to build the dismiss control’s accessible name. */
  label: string;
  /** Color of the chip. Gray when not provided. @default undefined */
  color?: ChipColor;
  /** Raw count rendered in a trailing CountBadge. Hidden when zero or negative. */
  count?: number;
  disabled?: boolean;
  /** Trailing icon. */
  endIcon?: SharedIconProps;
  /**
   * Filled surface (muted background) vs outlined placeholder.
   * @default true
   */
  filled?: boolean;
  /** Overflow threshold for `count`. Uncapped by default. */
  maxCount?: number;
  /** Leading icon. */
  startIcon?: SharedIconProps;
};

type ChipTriggerProps = ChipBaseProps & {
  /** Makes the chip the interactive control. Mutually exclusive with `onDismiss`. */
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  onDismiss?: never;
  /**
   * Fires when `endIcon` is activated, as a control separate from the
   * trigger. Renders `endIcon` as its own focusable button (sibling to the
   * trigger button, inside a non-interactive wrapper) instead of as
   * decoration — give it a `label` for its accessible name.
   */
  onEndIconClick?: () => void;
};

type ChipDismissibleProps = ChipBaseProps & {
  onClick?: never;
  /** When set, renders a dismiss control that removes the chip. */
  onDismiss: () => void;
  onEndIconClick?: never;
};

type ChipStaticProps = ChipBaseProps & {
  onClick?: never;
  onDismiss?: never;
  onEndIconClick?: never;
};

export type ChipProps =
  | ChipTriggerProps
  | ChipDismissibleProps
  | ChipStaticProps;

function isChipControlClick(target: EventTarget | null) {
  return (
    target instanceof Element &&
    (Boolean(target.closest('.eds-chip--dismiss')) ||
      Boolean(target.closest('.eds-chip--control')))
  );
}

function ChipIcon({ icon }: { icon: SharedIconProps }) {
  return (
    <span className="eds-chip--icon">
      <Icon filled={icon.filled} icon={icon.key} label={icon.label} size="s" />
    </span>
  );
}

export const Chip = memo(
  forwardRef<HTMLElement, ChipProps>(function Chip(
    {
      color,
      count,
      disabled,
      endIcon,
      filled = true,
      label,
      startIcon,
      maxCount,
      onClick,
      onDismiss,
      onEndIconClick,
    },
    forwardedRef,
  ) {
    const intl = useIntl();
    const dismissLabel = onDismiss
      ? intl.formatMessage(messages.dismiss, { label })
      : undefined;
    const isTrigger = onClick !== undefined;
    const hasEndIconAction =
      isTrigger && Boolean(endIcon) && Boolean(onEndIconClick);
    const hasTrailingControl = onDismiss !== undefined || hasEndIconAction;
    const hasCount = count !== undefined && count > 0;
    // The badge only sits flush against the chip edge when nothing follows it.
    const isCountAttached = hasCount && !hasTrailingControl;
    const labelTone = !filled && !disabled ? 'subdued' : 'auto';

    const className = clsx(
      'eds-chip',
      disabled && 'eds-chip--disabled',
      hasTrailingControl && 'eds-chip--dismissible',
      isCountAttached && 'eds-chip--has-count',
      !filled && 'eds-chip--outlined',
      isTrigger && !hasEndIconAction && 'eds-chip--trigger',
      color && `eds-chip--color-${color}`,
    );

    const blockLabelActivation = (event: MouseEvent<HTMLSpanElement>) => {
      if (!hasTrailingControl || isChipControlClick(event.target)) return;
      // Nested labelable controls inside a <label> make the whole label activate
      // the first button (dismiss). Block that; Select listens in capture.
      event.preventDefault();
      event.stopPropagation();
    };

    const stopTriggerToggle = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const handleDismiss = (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onDismiss?.();
    };

    const handleEndIconClick = () => {
      onEndIconClick?.();
    };

    const content = (
      <>
        {startIcon ? <ChipIcon icon={startIcon} /> : null}
        <Text tone={labelTone} truncate={1} variant="body-m">
          {label}
        </Text>
        {endIcon && !hasEndIconAction ? <ChipIcon icon={endIcon} /> : null}
        {hasCount ? (
          <CountBadge
            count={count}
            maxCount={maxCount}
            __private={{
              color,
              mode: isCountAttached ? 'attached' : undefined,
            }}
          />
        ) : null}
      </>
    );

    if (hasEndIconAction && endIcon) {
      return (
        <span
          aria-label={label}
          className={className}
          ref={forwardedRef as Ref<HTMLSpanElement>}
          role="group"
          onClickCapture={blockLabelActivation}
          onMouseDownCapture={blockLabelActivation}
        >
          <button
            className="eds-chip--control"
            disabled={disabled}
            type="button"
            onClick={onClick}
          >
            {content}
          </button>
          <button
            aria-label={endIcon.label}
            className="eds-chip--dismiss"
            disabled={disabled}
            type="button"
            onClick={handleEndIconClick}
            onMouseDown={stopTriggerToggle}
          >
            <Icon filled={endIcon.filled} icon={endIcon.key} size="s" />
          </button>
        </span>
      );
    }

    if (isTrigger) {
      return (
        <button
          className={className}
          disabled={disabled}
          ref={forwardedRef as Ref<HTMLButtonElement>}
          type="button"
          onClick={onClick}
        >
          {content}
        </button>
      );
    }

    return (
      <span
        aria-label={onDismiss ? label : undefined}
        className={className}
        ref={forwardedRef as Ref<HTMLSpanElement>}
        role={onDismiss ? 'group' : undefined}
        onClickCapture={blockLabelActivation}
        onMouseDownCapture={blockLabelActivation}
      >
        {content}
        {onDismiss ? (
          <button
            aria-label={dismissLabel}
            className="eds-chip--dismiss"
            disabled={disabled}
            type="button"
            onClick={handleDismiss}
            onMouseDown={stopTriggerToggle}
          >
            <Icon icon="x" size="s" />
          </button>
        ) : null}
      </span>
    );
  }),
);

Chip.displayName = 'Chip';
